import React, { useMemo } from 'react';
import { AlertCircle, Wand2, Package } from 'lucide-react';
import type { PickupBox, PickupRequestItem } from '../../../types';
import Select from '../../common/Select';

interface PickupDetailedStepProps {
    items: PickupRequestItem[];
    boxes: PickupBox[];
    assignments: Record<number, Record<number, number>>;
    onAssignmentsChange: (a: Record<number, Record<number, number>>) => void;
    attemptedSubmit: boolean;
}

const PickupDetailedStep: React.FC<PickupDetailedStepProps> = ({
    items,
    boxes,
    assignments,
    onAssignmentsChange,
    attemptedSubmit = false,
}) => {
    const showErrors = attemptedSubmit;

    const getItemTotal = (itemId: number) =>
        Object.values(assignments[itemId] || {}).reduce((a, b) => a + b, 0);

    const getBoxTotal = (boxId: number) =>
        Object.values(assignments).reduce(
            (sum, row) => sum + (row[boxId] || 0),
            0
        );

    const setCell = (itemId: number, boxId: number, qty: number) => {
        onAssignmentsChange({
            ...assignments,
            [itemId]: {
                ...(assignments[itemId] || {}),
                [boxId]: Math.max(0, qty),
            },
        });
    };

    const fillBox = (itemId: number, boxId: number) => {
        const item = items.find(i => i.id === itemId);
        if (!item) return;
        setCell(itemId, boxId, item.quantity);
    };

    const distributeEvenly = (itemId: number) => {
        const item = items.find(i => i.id === itemId);
        if (!item || boxes.length === 0) return;

        const qty = item.quantity;
        const perBox = Math.floor(qty / boxes.length);
        let remainder = qty % boxes.length;

        const row: Record<number, number> = {};
        boxes.forEach(box => {
            let n = perBox;
            if (remainder > 0) {
                n += 1;
                remainder -= 1;
            }
            row[box.id] = n;
        });

        onAssignmentsChange({ ...assignments, [itemId]: row });
    };

    const allItemsValid = useMemo(
        () => items.every(i => getItemTotal(i.id) === i.quantity),
        [items, assignments]
    );

    const allBoxesValid = useMemo(
        () => boxes.every(b => getBoxTotal(b.id) > 0),
        [boxes, assignments]
    );


    const allValid = allItemsValid && allBoxesValid;

    const remainingPerItem = (item: PickupRequestItem) =>
        item.quantity - getItemTotal(item.id);

    return (
        <div className="space-y-6">
            <p className="text-sm text-gray-500">
                Assign each item's quantity to the box(es) it will be packed in.
                Every item must be fully assigned and no box can be empty.
            </p>

            <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full min-w-[600px] border-separate border-spacing-0">
                    <thead>
                        <tr>
                            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide px-3 py-2 sticky left-0 bg-white z-10">
                                Item
                            </th>
                            {boxes.map(box => (
                                <th
                                    key={box.id}
                                    className="text-center text-xs font-medium text-gray-500 uppercase tracking-wide px-3 py-2 min-w-[90px]"
                                >
                                    Box {box.index}
                                </th>
                            ))}
                            <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wide px-3 py-2 min-w-[80px]">
                                Total
                            </th>
                            <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wide px-3 py-2">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(item => {
                            const assigned = getItemTotal(item.id);
                            const valid = assigned === item.quantity;
                            const remaining = remainingPerItem(item);

                            return (
                                <tr key={item.id} className="border-t border-gray-100">
                                    <td className="px-3 py-3 align-top sticky left-0 bg-white z-10">
                                        <div className="flex items-start gap-2 min-w-[180px]">
                                            <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden border border-gray-200 bg-gray-50">
                                                {item.variant_detail?.image_url || item.product_detail?.image_url ? (
                                                    <img
                                                        src={item.variant_detail?.image_url || item.product_detail?.image_url || ''}
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <Package className="h-4 w-4 text-gray-300" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm text-gray-900 truncate" title={item.product_detail?.title ?? `Product #${item.product}`}>
                                                    {item.product_detail?.title ?? `Product #${item.product}`}
                                                </p>
                                                {item.variant_detail && (
                                                    <p className="text-xs text-gray-500">
                                                        {item.variant_detail.name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {boxes.map(box => (
                                        <td key={box.id} className="px-3 py-3 text-center align-middle">
                                            <input
                                                type="number"
                                                min={0}
                                                max={item.quantity}
                                                value={assignments[item.id]?.[box.id] ?? ''}
                                                onChange={(e) => {
                                                    const qty = parseInt(e.target.value, 10) || 0;
                                                    setCell(item.id, box.id, qty);
                                                }}
                                                onFocus={(e) => e.target.select()}
                                                className={`w-16 px-2 py-1.5 border rounded-lg text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${attemptedSubmit && !valid
                                                        ? 'border-red-300 bg-red-50 text-red-700'
                                                        : 'border-gray-300'
                                                    }`}
                                            />
                                        </td>
                                    ))}

                                    <td className="px-3 py-3 text-center align-middle">
                                        <span className={`text-sm font-medium ${showErrors && !valid ? 'text-red-600' : 'text-gray-700'
                                            }`}>
                                            {assigned} / {item.quantity}
                                        </span>
                                    </td>

                                    <td className="px-3 py-3 text-right align-middle">
                                        <div className="flex items-center justify-end gap-2">
                                            {remaining > 0 && (
                                                <Select
                                                    value=""
                                                    onChange={(value) => {
                                                        if (!value) return;
                                                        const boxId = Number(value);
                                                        const remaining = item.quantity - getItemTotal(item.id);
                                                        setCell(item.id, boxId, (assignments[item.id]?.[boxId] || 0) + remaining);
                                                    }}
                                                    options={[
                                                        ...boxes.map(b => ({ value: String(b.id), label: `Box ${b.index}` })),
                                                    ]}
                                                    placeholder="Fill…"
                                                    className="w-32"
                                                />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => distributeEvenly(item.id)}
                                                title="Distribute evenly across all boxes"
                                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            >
                                                <Wand2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="border-t-2 border-gray-200">
                            <td className="px-3 py-3 sticky left-0 bg-white z-10 text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Box Total
                            </td>
                            {boxes.map(box => {
                                const total = getBoxTotal(box.id);
                                const empty = total === 0;
                                return (
                                    <td key={box.id} className="px-3 py-3 text-center">
                                        <span className={`text-sm font-medium ${(empty && showErrors) ? 'text-red-600' : 'text-gray-700'}`}>
                                            {total}
                                        </span>
                                    </td>
                                );
                            })}
                            <td />
                            <td />
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* Validation summary */}
            {attemptedSubmit && !allValid && (
                <div className="space-y-1">
                    {!allItemsValid && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            Some items are not fully assigned
                        </p>
                    )}
                    {!allBoxesValid && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            Some boxes are empty
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default PickupDetailedStep;